import React, { useState, useRef, useEffect } from 'react'
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '../../utils/cn'

const DateRangeSelector = ({
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onRangeChange,
  minDate,
  maxDate,
  className,
  labelClassName,
  inputClassName,
  showLabels = true,
  fromLabel = 'From Date',
  toLabel = 'To Date',
  placeholder = 'Select date',
  disabled = false,
  required = false,
  error,
  compact = true, // Compact layout for filter bars
}) => {
  // Helper function to get today's date at midnight
  const getTodayDate = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
  }

  // Initialize with today's date if fromDate is not provided
  const [localFromDate, setLocalFromDate] = useState(
    fromDate !== undefined ? fromDate : getTodayDate()
  )
  const [localToDate, setLocalToDate] = useState(toDate || null)
  const [fromDatePickerOpen, setFromDatePickerOpen] = useState(false)
  const [toDatePickerOpen, setToDatePickerOpen] = useState(false)
  const [currentFromMonth, setCurrentFromMonth] = useState(
    fromDate ? new Date(fromDate.getFullYear(), fromDate.getMonth()) : new Date()
  )
  const [currentToMonth, setCurrentToMonth] = useState(
    toDate ? new Date(toDate.getFullYear(), toDate.getMonth()) : new Date()
  )

  const fromDatePickerRef = useRef(null)
  const toDatePickerRef = useRef(null)
  const lastCheckedDateRef = useRef(null)

  // Helper function to trigger callbacks with updated values
  const triggerCallbacks = (today, prevToDate, onFromDateChange, onRangeChange) => {
    if (onFromDateChange) {
      onFromDateChange(today)
    }
    if (onRangeChange) {
      onRangeChange(today, prevToDate)
    }
  }

  // Helper function to check if date is today
  const isDateToday = (date, today) => {
    if (!date) return false
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    return dateOnly.getTime() === todayOnly.getTime()
  }

  // Helper function to update from date to today
  const updateFromDateToToday = (today, setCurrentFromMonth, setLocalToDate, onFromDateChange, onRangeChange) => {
    setCurrentFromMonth(new Date(today.getFullYear(), today.getMonth()))
    
    setLocalToDate(prevToDate => {
      triggerCallbacks(today, prevToDate, onFromDateChange, onRangeChange)
      return prevToDate
    })
    
    return today
  }

  // Helper function to process from date update
  const processFromDateUpdate = (prevFromDate, today, setCurrentFromMonth, setLocalToDate, onFromDateChange, onRangeChange) => {
    const currentFrom = prevFromDate ? new Date(prevFromDate) : null
    const currentFromDate = currentFrom ? new Date(currentFrom.getFullYear(), currentFrom.getMonth(), currentFrom.getDate()) : null
    
    if (!isDateToday(currentFromDate, today)) {
      return updateFromDateToToday(today, setCurrentFromMonth, setLocalToDate, onFromDateChange, onRangeChange)
    }
    
    return prevFromDate
  }

  // Auto-update fromDate to today's date daily (for uncontrolled component or null value)
  useEffect(() => {
    if (fromDate !== undefined && fromDate !== null) {
      return
    }

    const updateToToday = () => {
      const today = getTodayDate()
      const todayDateString = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`
      
      if (lastCheckedDateRef.current === todayDateString) {
        return
      }

      lastCheckedDateRef.current = todayDateString
      
      setLocalFromDate(prevFromDate => 
        processFromDateUpdate(
          prevFromDate,
          today,
          setCurrentFromMonth,
          setLocalToDate,
          onFromDateChange,
          onRangeChange
        )
      )
    }

    updateToToday()
    const intervalId = setInterval(updateToToday, 60000)

    return () => clearInterval(intervalId)
  }, [fromDate, onFromDateChange, onRangeChange])

  // Sync with controlled props
  useEffect(() => {
    if (fromDate !== undefined) {
      setLocalFromDate(fromDate)
      if (fromDate) {
        setCurrentFromMonth(new Date(fromDate.getFullYear(), fromDate.getMonth()))
      }
    }
  }, [fromDate])

  useEffect(() => {
    if (toDate !== undefined) {
      setLocalToDate(toDate)
      if (toDate) {
        setCurrentToMonth(new Date(toDate.getFullYear(), toDate.getMonth()))
      }
    }
  }, [toDate])

  // Close pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        fromDatePickerRef.current &&
        !fromDatePickerRef.current.contains(event.target)
      ) {
        setFromDatePickerOpen(false)
      }
      if (
        toDatePickerRef.current &&
        !toDatePickerRef.current.contains(event.target)
      ) {
        setToDatePickerOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const formatDate = (date) => {
    if (!date) return ''
    const options = { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    }
    return date.toLocaleDateString('en-US', options)
  }

  const formatDateInput = (date) => {
    if (!date) return ''
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const parseDateInput = (dateString) => {
    if (!dateString) return null
    const date = new Date(dateString)
    return isNaN(date.getTime()) ? null : date
  }

  const handleFromDateChange = (date) => {
    const newDate = date ? new Date(date) : null
    setLocalFromDate(newDate)
    if (onFromDateChange) {
      onFromDateChange(newDate)
    }
    if (onRangeChange) {
      onRangeChange(newDate, localToDate)
    }
    // If to date is before new from date, clear to date
    if (newDate && localToDate && newDate > localToDate) {
      const clearedToDate = null
      setLocalToDate(clearedToDate)
      if (onToDateChange) {
        onToDateChange(clearedToDate)
      }
      if (onRangeChange) {
        onRangeChange(newDate, clearedToDate)
      }
    }
    setFromDatePickerOpen(false)
  }

  const handleToDateChange = (date) => {
    const newDate = date ? new Date(date) : null
    // Validate that to date is not before from date
    if (newDate && localFromDate && newDate < localFromDate) {
      return
    }
    setLocalToDate(newDate)
    if (onToDateChange) {
      onToDateChange(newDate)
    }
    if (onRangeChange) {
      onRangeChange(localFromDate, newDate)
    }
    setToDatePickerOpen(false)
  }


  const navigateMonth = (direction, isFrom) => {
    if (isFrom) {
      setCurrentFromMonth(
        new Date(
          currentFromMonth.getFullYear(),
          currentFromMonth.getMonth() + direction
        )
      )
    } else {
      setCurrentToMonth(
        new Date(
          currentToMonth.getFullYear(),
          currentToMonth.getMonth() + direction
        )
      )
    }
  }

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay()
  }

  const isDateDisabled = (date, isFrom) => {
    if (minDate && date < minDate) return true
    if (maxDate && date > maxDate) return true
    if (isFrom && localToDate && date > localToDate) return false
    if (!isFrom && localFromDate && date < localFromDate) return true
    return false
  }

  const isDateSelected = (date, isFrom) => {
    if (isFrom) {
      return localFromDate && date.toDateString() === localFromDate.toDateString()
    } else {
      return localToDate && date.toDateString() === localToDate.toDateString()
    }
  }

  const isDateInRange = (date) => {
    if (!localFromDate || !localToDate) return false
    return date >= localFromDate && date <= localToDate
  }

  const renderCalendar = (isFrom) => {
    const currentMonth = isFrom ? currentFromMonth : currentToMonth
    const daysInMonth = getDaysInMonth(currentMonth)
    const firstDay = getFirstDayOfMonth(currentMonth)
    const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    const days = []

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-full aspect-square" />)
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
      const disabled = isDateDisabled(date, isFrom)
      const selected = isDateSelected(date, isFrom)
      const inRange = isDateInRange(date)
      const isToday = date.toDateString() === new Date().toDateString()

      days.push(
        <button
          key={day}
          type="button"
          onClick={() => !disabled && (isFrom ? handleFromDateChange(date) : handleToDateChange(date))}
          disabled={disabled}
          className={cn(
            "w-full aspect-square rounded-md text-xs sm:text-sm font-medium transition-colors",
            "hover:bg-accent hover:text-accent-foreground",
            disabled && "opacity-30 cursor-not-allowed",
            selected && "bg-primary text-primary-foreground hover:bg-primary/90",
            inRange && !selected && "bg-primary/10 text-primary",
            isToday && !selected && "ring-2 ring-primary/50",
            !disabled && !selected && "hover:bg-accent"
          )}
        >
          {day}
        </button>
      )
    }

    return (
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 sm:p-4 w-full min-w-[260px] max-w-[300px]">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => navigateMonth(-1, isFrom)}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          </button>
          <h3 className="text-sm sm:text-base font-semibold text-gray-900">
            {monthName}
          </h3>
          <button
            type="button"
            onClick={() => navigateMonth(1, isFrom)}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Next month"
          >
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div
              key={day}
              className="text-xs sm:text-sm font-medium text-gray-500 text-center py-1"
            >
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days}
        </div>
      </div>
    )
  }

  const inputBaseClass = cn(
    "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-9 text-sm cursor-pointer",
    "focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    error && "border-red-500 focus:border-red-500 focus:ring-red-200",
    inputClassName
  )

  return (
    <div className={cn("w-full", compact && "max-w-md", className)}>
      <div className={cn("flex flex-col sm:flex-row gap-3 sm:gap-3", compact && "sm:gap-3")}>
        {/* From Date Field */}
        <div className={cn("flex-1", compact && "min-w-0")}>
          {showLabels && (
            <label
              htmlFor="from-date"
              className={cn(
                "block text-xs font-medium text-gray-600 mb-1",
                compact && "sm:mb-1",
                labelClassName
              )}
            >
              {fromLabel}
            </label>
          )}
          <div className="relative" ref={fromDatePickerRef}>
            <div className="relative">
              <input
                id="from-date"
                type="text"
                value={localFromDate ? formatDate(localFromDate) : ''}
                onChange={(e) => {
                  const date = parseDateInput(e.target.value)
                  if (date) handleFromDateChange(date)
                  else if (e.target.value === '') handleFromDateChange(null)
                }}
                onFocus={() => setFromDatePickerOpen(true)}
                onClick={() => setFromDatePickerOpen(true)}
                placeholder={placeholder}
                disabled={disabled}
                required={required}
                readOnly
                className={inputBaseClass}
              />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                <Calendar className="w-4 h-4" />
              </span>
            </div>
            {fromDatePickerOpen && (
              <div className="absolute z-[9999] mt-1.5 left-0 shadow-lg rounded-lg overflow-hidden">
                {renderCalendar(true)}
              </div>
            )}
          </div>
          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>

        {/* To Date Field */}
        <div className={cn("flex-1", compact && "min-w-0")}>
          {showLabels && (
            <label
              htmlFor="to-date"
              className={cn(
                "block text-xs font-medium text-gray-600 mb-1",
                compact && "sm:mb-1",
                labelClassName
              )}
            >
              {toLabel}
            </label>
          )}
          <div className="relative" ref={toDatePickerRef}>
            <div className="relative">
              <input
                id="to-date"
                type="text"
                value={localToDate ? formatDate(localToDate) : ''}
                onChange={(e) => {
                  const date = parseDateInput(e.target.value)
                  if (date) handleToDateChange(date)
                  else if (e.target.value === '') handleToDateChange(null)
                }}
                onFocus={() => setToDatePickerOpen(true)}
                onClick={() => setToDatePickerOpen(true)}
                placeholder={placeholder}
                disabled={disabled}
                required={required}
                readOnly
                className={inputBaseClass}
              />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                <Calendar className="w-4 h-4" />
              </span>
            </div>
            {toDatePickerOpen && (
              <div className="absolute z-[9999] mt-1.5 left-0 shadow-lg rounded-lg overflow-hidden">
                {renderCalendar(false)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DateRangeSelector

